import {ReviewStatus} from 'graphql-client/__generated/graphqlGlobalTypes';
import ReviewConstants from 'notebook/reviews/ReviewConstants';

/**
 * The subset of a template's or sub-template's editor state (`TemplateStore.getEditorState(id)` or
 * `SubtemplateStore.getEditorState(id)`) needed to decide editability. Kept structural so it can be
 * satisfied by either store's editor state or a plain object in tests.
 */
export interface TemplateEditableState {
  // `template` can be null while an id is registered but its payload is still loading.
  template: {
    permissions: {writable?: boolean};
    review?: {status?: ReviewStatus} | null;
  } | null;
  isLatest: boolean;
  dirty: boolean;
}

const LOCKED_STATUSES: ReadonlyArray<ReviewStatus> =
  ReviewConstants.LOCKED_STATUSES as ReadonlyArray<ReviewStatus>;

/**
 * Single source of truth for "can the user edit this template or sub-template right now".
 *
 * Mirrors TemplateFile.allowEdits() and SubtemplateFile.allowEdits():
 * writable && latest && !dirty && !locked-for-review.
 * Returns false when the payload is still null (e.g. during async open/load), since a
 * not-yet-loaded template cannot be edited.
 */
export function canEditTemplate(state: TemplateEditableState): boolean {
  const {template, isLatest, dirty} = state;
  if (template == null) {
    return false;
  }
  const reviewStatus = template.review?.status;
  const isLockedForReview = reviewStatus != null && LOCKED_STATUSES.includes(reviewStatus);
  return Boolean(template.permissions.writable) && isLatest && !dirty && !isLockedForReview;
}
